import {Icon, type IconName} from './Icon'
import {cva, type VariantProps} from 'class-variance-authority'

const labelVariants = cva(
  'inline-flex shrink-0 items-center gap-4 rounded-md text-sm text-xs leading-xs font-light',
  {
    variants: {
      size: {
        s: 'h-22',
        m: 'h-28',
      },
      mobileSize: {
        s: 'max-md:h-22',
        m: 'max-md:h-28',
      },
      color: {
        red: 'bg-surface-error text-text-error',
        green: 'bg-surface-success text-text-success',
        blue: 'bg-surface-info text-text-info',
        grey: 'bg-surface-alt text-text',
        transparent: 'bg-surface-transparent text-text',
      },
    },
    defaultVariants: {
      size: 'm',
      color: 'grey',
    },
  },
)

const iconVariants = cva('', {
  variants: {
    size: {
      s: 'h-16',
      m: 'h-20',
    },
    mobileSize: {
      s: 'max-md:!h-16',
      m: 'max-md:!h-20',
    },
  },
})

export interface LabelProps
  extends Omit<React.HTMLAttributes<HTMLSpanElement>, 'color'>,
    VariantProps<typeof labelVariants> {
  text?: string
  icon?: IconName
  /** Size to use below the `md` breakpoint. Falls back to `size` when not set. */
  mobileSize?: 's' | 'm'
}

function getLabelPadding(hasIcon: boolean, hasText: boolean): string {
  if (hasIcon && !hasText) return 'px-4'
  if (hasIcon && hasText) return 'pl-6 pr-8'
  return 'px-8'
}

/**
 * Figma: https://www.figma.com/design/qS60CfMVFGs07l2G3yfhsB/%F0%9F%92%A0-Components?node-id=16878-59275&m=dev
 */
export function Label({size, color, text, icon, mobileSize, className, ...props}: LabelProps) {
  const padding = getLabelPadding(!!icon, !!text)

  return (
    <span
      className={labelVariants({
        size,
        mobileSize,
        color,
        className: `${padding} ${className ?? ''}`,
      })}
      {...props}
    >
      {icon && (
        <Icon
          name={icon}
          size={(size ?? 'm') === 's' ? 16 : 20}
          color="current"
          className={iconVariants({size, mobileSize}) || undefined}
        />
      )}
      {text}
    </span>
  )
}
